import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { shiftSwapsApi, schedulesApi, usersApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowsClockwise, Check, X, Warning } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const STATUS_MAP = {
  pending: { l: 'In Attesa', c: 'border-[#F59E0B] text-[#F59E0B] bg-[#F59E0B]/5' },
  completed: { l: 'Completato', c: 'border-[#22C55E] text-[#22C55E] bg-[#22C55E]/5' },
  rejected: { l: 'Rifiutato', c: 'border-[#FF2A00] text-[#FF2A00] bg-[#FF2A00]/5' },
};
const TYPE_LABELS = { office: 'Presente', smart_working: 'Smart Working', on_call: 'Reperibilità', leave: 'Ferie', permission: 'Permesso', overtime: 'Straordinario' };

const ShiftSwapsPage = () => {
  const { user, isTeamLeader, isManager } = useAuth();
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [mySchedules, setMySchedules] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [form, setForm] = useState({ schedule_id: '', target_user_id: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchSwaps = useCallback(async () => {
    setLoading(true);
    try { const { data } = await shiftSwapsApi.getAll(); setSwaps(data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSwaps(); }, [fetchSwaps]);

  const openCreate = async () => {
    try {
      const month = new Date().getMonth() + 1;
      const year = new Date().getFullYear();
      const [schedRes, usersRes] = await Promise.all([
        schedulesApi.getCalendar(month, year, null, 'personal'),
        usersApi.getAll(),
      ]);
      setMySchedules(schedRes.data.filter(s => s.user_id === user.id && s.status === 'approved'));
      setColleagues(usersRes.data.filter(u => u.id !== user.id));
      setForm({ schedule_id: '', target_user_id: '', reason: '' });
      setIsCreateOpen(true);
    } catch (e) { toast.error('Errore caricamento dati'); }
  };

  const handleCreate = async () => {
    if (!form.schedule_id || !form.target_user_id) return;
    setSubmitting(true);
    try {
      await shiftSwapsApi.create(form);
      toast.success('Proposta di scambio inviata');
      setIsCreateOpen(false);
      fetchSwaps();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const handleRespond = async (id, action) => {
    try { await shiftSwapsApi.respond(id, action); toast.success(action === 'accepted' ? 'Scambio accettato' : 'Scambio rifiutato'); fetchSwaps(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const handleConfirm = async (id, action) => {
    try { await shiftSwapsApi.confirm(id, action); toast.success(action === 'confirmed' ? 'Scambio confermato' : 'Scambio rifiutato'); fetchSwaps(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="shift-swaps-page">
      <Toaster position="top-right" richColors />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Scambio Turni</h1>
          <p className="text-[#52525B] mt-1 text-sm">Proponi e gestisci scambi di turno con i colleghi</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0A0A0A] text-white px-5 py-2.5 font-medium hover:bg-[#333] rounded-none" data-testid="new-swap-btn">
          <ArrowsClockwise size={18} weight="bold" className="mr-2" /> Proponi Scambio
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>
      ) : swaps.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#E4E4E7]">
          <ArrowsClockwise size={40} className="mx-auto text-[#E4E4E7] mb-3" />
          <p className="text-[#52525B]">Nessuno scambio turno</p>
        </div>
      ) : (
        <div className="space-y-3">
          {swaps.map(s => {
            const st = STATUS_MAP[s.status] || STATUS_MAP.pending;
            const isTarget = s.target_id === user?.id;
            const canRespond = isTarget && s.target_status === 'pending';
            const canConfirm = (isTeamLeader || isManager || user?.role === 'super_admin') && s.target_status === 'accepted' && s.manager_status === 'pending';
            return (
              <div key={s.id} className="bg-white border border-[#E4E4E7] p-4 md:p-5" data-testid={`swap-${s.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${st.c}`}>{st.l}</span>
                      <span className="text-xs text-[#A1A1AA] font-mono">{s.created_at?.split('T')[0]}</span>
                    </div>
                    <p className="text-sm">
                      <strong>{s.proposer_name}</strong>
                      <span className="mx-2 text-[#A1A1AA]">→</span>
                      <strong>{s.target_name}</strong>
                    </p>
                    <p className="text-xs text-[#52525B] mt-1">
                      {TYPE_LABELS[s.schedule_type] || s.schedule_type} — {s.start_date} → {s.end_date}
                    </p>
                    {s.reason && <p className="text-xs text-[#A1A1AA] mt-1">Motivo: {s.reason}</p>}
                    <div className="flex gap-4 mt-2 text-[10px] font-bold uppercase tracking-wider">
                      <span>Destinatario: <span className={s.target_status === 'accepted' ? 'text-[#22C55E]' : s.target_status === 'rejected' ? 'text-[#FF2A00]' : 'text-[#F59E0B]'}>{s.target_status}</span></span>
                      <span>Responsabile: <span className={s.manager_status === 'confirmed' ? 'text-[#22C55E]' : s.manager_status === 'rejected' ? 'text-[#FF2A00]' : 'text-[#F59E0B]'}>{s.manager_status}</span></span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {canRespond && (
                      <>
                        <Button size="sm" onClick={() => handleRespond(s.id, 'accepted')}
                          className="bg-[#22C55E] text-white hover:bg-[#16A34A] rounded-none" data-testid={`accept-swap-${s.id}`}>
                          <Check size={16} weight="bold" className="mr-1" /> Accetta
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleRespond(s.id, 'rejected')}
                          className="border-[#FF2A00] text-[#FF2A00] hover:bg-[#FF2A00] hover:text-white rounded-none" data-testid={`reject-swap-${s.id}`}>
                          <X size={16} weight="bold" className="mr-1" /> Rifiuta
                        </Button>
                      </>
                    )}
                    {canConfirm && (
                      <>
                        <Button size="sm" onClick={() => handleConfirm(s.id, 'confirmed')}
                          className="bg-[#002FA7] text-white hover:bg-[#00278A] rounded-none" data-testid={`confirm-swap-${s.id}`}>
                          <Check size={16} weight="bold" className="mr-1" /> Conferma
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleConfirm(s.id, 'rejected')}
                          className="border-[#FF2A00] text-[#FF2A00] rounded-none" data-testid={`deny-swap-${s.id}`}>
                          <X size={16} weight="bold" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-lg rounded-none border border-[#0A0A0A]">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Proponi Scambio Turno</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Turno da scambiare</Label>
              <Select value={form.schedule_id} onValueChange={v => setForm({...form, schedule_id: v})}>
                <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="swap-schedule"><SelectValue placeholder="Seleziona turno" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {mySchedules.map(s => (
                    <SelectItem key={s.id} value={s.id} className="rounded-none">
                      {TYPE_LABELS[s.schedule_type] || s.schedule_type} — {s.start_date} → {s.end_date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Collega destinatario</Label>
              <Select value={form.target_user_id} onValueChange={v => setForm({...form, target_user_id: v})}>
                <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="swap-target"><SelectValue placeholder="Seleziona collega" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {colleagues.map(u => <SelectItem key={u.id} value={u.id} className="rounded-none">{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Motivo (opzionale)</Label>
              <Input value={form.reason} onChange={e => setForm({...form, reason: e.target.value})}
                placeholder="Es: Impegno personale" className="rounded-none border-[#E4E4E7]" data-testid="swap-reason" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
              <Button disabled={submitting || !form.schedule_id || !form.target_user_id} onClick={handleCreate}
                className="flex-1 bg-[#0A0A0A] text-white hover:bg-[#333] rounded-none" data-testid="submit-swap-btn">
                {submitting ? 'Invio...' : 'Proponi Scambio'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftSwapsPage;
